import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements, PaymentElement, useStripe, useElements,
} from '@stripe/react-stripe-js';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Zap, CreditCard, Loader2, Check, AlertTriangle, X,
  TrendingUp, ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STRIPE_PK = process.env.REACT_APP_STRIPE_PK;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });
const fmtUSD = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Lazily-loaded stripe client (only when the auto-topup card mounts).
let _stripePromise;
const stripePromise = () => {
  if (!_stripePromise && STRIPE_PK) _stripePromise = loadStripe(STRIPE_PK);
  return _stripePromise;
};

/**
 * Auto top-up card for the Org Dashboard Wallet tab.
 * Wraps Stripe Elements card capture + threshold/amount settings.
 */
export default function AutoTopupCard({ onChange }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/organizations/me/wallet/auto-topup`, auth());
      setSettings(r.data);
    } catch (_) {
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (enable) => {
    if (enable) {
      // Always go through the setup dialog — if a card already exists,
      // the dialog reuses it via Stripe's existing customer; otherwise
      // the org enters new card details.
      setShowSetup(true);
      return;
    }
    try {
      await axios.post(
        `${API}/organizations/me/wallet/auto-topup/disable`,
        {}, auth(),
      );
      toast.success('Auto top-up disabled');
      load();
      onChange && onChange();
    } catch (e) {
      toast.error('Could not disable auto top-up');
    }
  };

  if (!STRIPE_PK) {
    return null;     // hide entirely on envs without a Stripe publishable key
  }

  return (
    <>
      <Card className="border-border" data-testid="auto-topup-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="h-9 w-9 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center shrink-0">
                <Zap className="h-4 w-4 text-indigo-300" />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-base">Auto top-up</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Stay funded — when your balance drops below a threshold,
                  we'll top up your wallet from a saved card automatically.
                </p>
              </div>
            </div>
            <Switch
              checked={!!settings?.enabled}
              onCheckedChange={toggle}
              disabled={loading}
              data-testid="auto-topup-toggle"
            />
          </div>

          {settings?.enabled && settings?.card && (
            <div className="bg-card/60 border border-border rounded-md p-3 space-y-2" data-testid="auto-topup-summary">
              <Row label="Trigger" value={`Balance < ${fmtUSD(settings.threshold_cents)}`} />
              <Row label="Top-up amount" value={fmtUSD(settings.amount_cents)} />
              <Row
                label="Card"
                value={
                  <span className="flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5" />
                    <span className="uppercase">{settings.card.brand}</span> •••• {settings.card.last4}
                    <span className="text-muted-foreground/70">
                      ({String(settings.card.exp_month).padStart(2, '0')}/{String(settings.card.exp_year).slice(-2)})
                    </span>
                  </span>
                }
              />
              {settings.last_status === 'failed' && settings.last_error && (
                <p className="text-xs text-rose-300 flex items-start gap-1 pt-1" data-testid="auto-topup-error">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  Last attempt failed: {settings.last_error}
                </p>
              )}
              {settings.last_status === 'succeeded' && settings.last_amount_cents && (
                <p className="text-xs text-emerald-300 flex items-center gap-1 pt-1" data-testid="auto-topup-last-success">
                  <Check className="h-3.5 w-3.5" />
                  Last auto top-up: {fmtUSD(settings.last_amount_cents)}
                </p>
              )}
              <button
                type="button"
                onClick={() => setShowSetup(true)}
                className="text-xs text-indigo-300 hover:underline"
                data-testid="auto-topup-edit-btn"
              >
                Update card or thresholds
              </button>
            </div>
          )}

          {!settings?.enabled && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> Cards are processed by Stripe.
              Eventism never sees your card number.
            </p>
          )}
        </CardContent>
      </Card>

      <AutoTopupSetupDialog
        open={showSetup}
        onClose={() => setShowSetup(false)}
        onSaved={() => { setShowSetup(false); load(); onChange && onChange(); }}
        defaults={settings}
      />
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Setup dialog
// ---------------------------------------------------------------------------

function AutoTopupSetupDialog({ open, onClose, onSaved, defaults }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [loadingIntent, setLoadingIntent] = useState(false);

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      return;
    }
    setLoadingIntent(true);
    (async () => {
      try {
        const r = await axios.post(
          `${API}/organizations/me/wallet/auto-topup/setup-intent`,
          {}, auth(),
        );
        setClientSecret(r.data.client_secret);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not start card setup');
        onClose();
      } finally {
        setLoadingIntent(false);
      }
    })();
  }, [open, onClose]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="auto-topup-setup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-indigo-300" /> Set up auto top-up
          </DialogTitle>
          <DialogDescription>
            Add a card and pick thresholds. Your wallet will top up automatically
            so campaigns never stall mid-send.
          </DialogDescription>
        </DialogHeader>
        {loadingIntent || !clientSecret ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
          </div>
        ) : (
          <Elements
            stripe={stripePromise()}
            options={{
              clientSecret,
              appearance: { theme: 'night', variables: { colorPrimary: '#818cf8' } },
            }}
          >
            <AutoTopupForm onSaved={onSaved} defaults={defaults} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AutoTopupForm({ onSaved, defaults }) {
  const stripe = useStripe();
  const elements = useElements();
  const [threshold, setThreshold] = useState(defaults?.threshold_cents || 500);
  const [amount, setAmount] = useState(defaults?.amount_cents || 5000);
  const [submitting, setSubmitting] = useState(false);

  const thresholds = defaults?.presets?.thresholds_cents || [500, 1000, 2500, 5000];
  const amounts = defaults?.presets?.amounts_cents || [2000, 5000, 10000, 20000];

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      // 1) Confirm the SetupIntent — Stripe Elements collects the card
      //    + handles 3DS challenges client-side, returning a `pm_…` id.
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        toast.error(error.message || 'Card setup failed');
        return;
      }
      if (!setupIntent || setupIntent.status !== 'succeeded') {
        toast.error('Card setup incomplete — please try again');
        return;
      }
      // 2) Persist settings + PM on the backend
      await axios.post(
        `${API}/organizations/me/wallet/auto-topup/save`,
        {
          payment_method_id: setupIntent.payment_method,
          threshold_cents: threshold,
          amount_cents: amount,
        },
        auth(),
      );
      toast.success('Auto top-up enabled');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save settings');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-1">
          When balance drops below
        </p>
        <div className="grid grid-cols-4 gap-2">
          {thresholds.map((c) => (
            <PresetButton
              key={c} cents={c} active={threshold === c}
              onClick={() => setThreshold(c)}
              data-testid={`auto-topup-threshold-${c}`}
            />
          ))}
        </div>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-1">
          Top up this much
        </p>
        <div className="grid grid-cols-4 gap-2">
          {amounts.map((c) => (
            <PresetButton
              key={c} cents={c} active={amount === c}
              onClick={() => setAmount(c)}
              data-testid={`auto-topup-amount-${c}`}
            />
          ))}
        </div>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
          Card details
        </p>
        <div className="rounded-md border border-border bg-card/40 p-3" data-testid="auto-topup-card-element">
          <PaymentElement options={{ layout: 'tabs' }} />
        </div>
      </div>
      <Button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full h-11 bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white font-bold"
        data-testid="auto-topup-save-btn"
      >
        {submitting
          ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
          : <><TrendingUp className="h-4 w-4 mr-2" /> Save & enable</>}
      </Button>
      <p className="text-[10px] text-muted-foreground text-center flex items-center justify-center gap-1">
        <X className="h-3 w-3" /> Disable anytime · No subscription · Min {fmtUSD(thresholds[0])} threshold
      </p>
    </form>
  );
}

function PresetButton({ cents, active, onClick, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md py-2 text-sm font-semibold tabular-nums transition border ${
        active
          ? 'border-indigo-400 bg-indigo-500/15 text-indigo-100'
          : 'border-border bg-card/60 hover:border-indigo-500/40'
      }`}
      {...rest}
    >
      {fmtUSD(cents)}
    </button>
  );
}
